import { Badge } from '@/components/ui/badge';
import { ChevronRight, ChevronDown, Plus, Edit, Trash2 } from 'lucide-react';

// Flatten a nested tree into an ordered list of {node, depth} entries,
// respecting the currently-expanded state. This avoids recursive JSX which
// triggers `@babel/traverse` stack overflow on complex files in this
// webpack/babel toolchain version.
const flattenTree = (roots, expanded) => {
  const out = [];
  const walk = (list, depth) => {
    for (const n of list || []) {
      out.push({ node: n, depth });
      const isOpen = expanded[n.class_id] !== false;
      if (isOpen && n.children && n.children.length > 0) {
        walk(n.children, depth + 1);
      }
    }
  };
  walk(roots, 0);
  return out;
};

const ClassTreeNode = ({ roots, onSelect, onEdit, onDelete, onAddChild, selectedId, expanded, setExpanded }) => {
  const flat = flattenTree(roots, expanded);

  return (
    <div>
      {flat.map(({ node, depth }) => {
        const hasChildren = node.children && node.children.length > 0;
        const isOpen = expanded[node.class_id] !== false;
        const isSelected = selectedId === node.class_id;
        const selClass = isSelected ? 'bg-[#002FA7]/10 ring-1 ring-[#002FA7]/40' : '';
        const toggle = (e) => { e.stopPropagation(); setExpanded({ ...expanded, [node.class_id]: !isOpen }); };

        return (
          <div key={node.class_id} data-testid={`tree-node-${node.class_id}`}>
            <div
              className={`group flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-[#F3F4F6] cursor-pointer text-sm ${selClass}`}
              style={{ paddingLeft: 8 + depth * 18 }}
              onClick={() => onSelect(node)}
            >
              <button type="button" className="w-4 h-4 flex items-center justify-center text-[#9CA3AF] hover:text-[#4B5563]" onClick={toggle}>
                {hasChildren && isOpen && <ChevronDown className="w-3.5 h-3.5" />}
                {hasChildren && !isOpen && <ChevronRight className="w-3.5 h-3.5" />}
                {!hasChildren && <span className="w-3.5 h-3.5" />}
              </button>
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: node.color || '#002FA7' }} />
              <span className="flex-1 truncate font-medium text-[#111827]">{node.name}</span>
              <Badge variant="secondary" className="text-[9px] px-1.5 py-0">{node.individual_count || 0}</Badge>
              <div className="opacity-0 group-hover:opacity-100 flex gap-1">
                <button type="button" className="text-[#9CA3AF] hover:text-[#002FA7]" onClick={(e) => { e.stopPropagation(); onAddChild(node); }} title="Add child">
                  <Plus className="w-3.5 h-3.5" />
                </button>
                <button type="button" className="text-[#9CA3AF] hover:text-[#FF9500]" onClick={(e) => { e.stopPropagation(); onEdit(node); }} title="Edit">
                  <Edit className="w-3.5 h-3.5" />
                </button>
                <button type="button" className="text-[#9CA3AF] hover:text-[#FF3B30]" onClick={(e) => { e.stopPropagation(); onDelete(node); }} title="Delete">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ClassTreeNode;
